import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { VACANCY_OVERRIDE, type VacancyOverrideType } from "../constants";

import { Badge } from "@/components/ui/v2/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";

type OverrideBadgesProps = {
  type: VacancyOverrideType;
  isRedundant?: boolean;
  isOffline?: boolean;
  isUnpublished?: boolean;
  isAffected?: boolean;
};

export function OverrideBadges({
  type,
  isRedundant = false,
  isOffline = false,
  isUnpublished = false,
  isAffected = false,
}: OverrideBadgesProps) {
  const { t } = useTranslation();
  const isInclude = type === VACANCY_OVERRIDE.INCLUDE;
  const label = isInclude
    ? t("admin.segments.builder.exceptions.manuallyIncluded")
    : t("admin.segments.builder.exceptions.manuallyExcluded");
  const affectedMessage = isInclude
    ? t("admin.segments.builder.exceptions.affectedInclude")
    : t("admin.segments.builder.exceptions.affectedExclude");

  return (
    <>
      {isAffected ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="warning"
              tabIndex={0}
              className="gap-1"
              data-testid="override-badge"
            >
              <AlertTriangle aria-hidden className="size-3.5" />
              {label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {affectedMessage}
          </TooltipContent>
        </Tooltip>
      ) : (
        <Badge variant="outline" data-testid="override-badge">
          {label}
        </Badge>
      )}
      {isRedundant && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="warning" tabIndex={0} data-testid="redundant-badge">
              {isInclude
                ? t("admin.segments.builder.exceptions.rulesInclude")
                : t("admin.segments.builder.exceptions.rulesExclude")}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {t("admin.segments.builder.exceptions.redundantHelp")}
          </TooltipContent>
        </Tooltip>
      )}
      {isOffline && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="neutral" tabIndex={0}>
              {t("admin.segments.builder.exceptions.offline")}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {t("admin.segments.builder.exceptions.offlineHelp")}
          </TooltipContent>
        </Tooltip>
      )}
      {isUnpublished && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="neutral" tabIndex={0}>
              {t("common.unpublished")}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {t("admin.segments.builder.exceptions.unpublishedHelp")}
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}
